import React, { useCallback, useEffect, useState } from 'react';
import cn from 'classnames';
import { FormattedMessage } from 'react-intl';
import AuInput, { INPUT_TYPE_RADIOBUTTON } from '@au/core/lib/components/elements/AuInput';

import fieldset from '../../../css/fieldset.module.scss';

/**
 * RadioButtonsInput
 *
 * Example YAML usage:
 * ```yaml
 * attributes:
 *   sourceEnv:
 *     display:
 *       rules: required
 *       create:
 *         component: RadioButtonsInput
 *         props:
 *           defaultValue: dev
 *           options:
 *             - dev
 *             - staging
 * ```
 *
 * @param {string} [props.defaultValue]
 *   The option selected by default when the component first mounts.
 *   Configured via YAML `props.defaultValue`. If omitted, the first entry
 *   in `options` is used instead.
 * @param {string[]} [props.options=[]]
 *   The list of selectable values, rendered as one radio button per
 *   entry (using the option's own string value as both its `caption` and
 *   `key`). Configured via YAML `props.options`.
 *
 * @returns {JSX.Element} A labeled group of radio buttons.
 */
export const RadioButtonsInput = ({
  // generic props
  field,
  fieldChangeCallback,
  labelId,
  disabled,
  showErrors,
  validationErrors,
  // extra props specifically for this Input
  defaultValue,
  options = [],
}) => {
  const [value, setValue] = useState(defaultValue ?? options[0]);

  useEffect(() => {
    if (value === undefined && options.length > 0) {
      handleChange(options[0]);
    }
  }, [options]);

  const error = validationErrors && field in validationErrors ? validationErrors[field] : null;

  const handleChange = useCallback(
    (selected) => {
      setValue(selected);
      if (fieldChangeCallback) fieldChangeCallback(field, selected);
    },
    [field, fieldChangeCallback],
  );

  return (
    <div className={cn(fieldset.row, fieldset.row_flexible)}>
      {labelId && (
        <label className={fieldset.label}>
          <FormattedMessage id={labelId} />
        </label>
      )}
      <div className={cn(fieldset.control, fieldset.radio_control)}>
        <div className={fieldset.group}>
          {options.map((option) => (
            <div key={option} className={fieldset.radio}>
              <AuInput
                type={INPUT_TYPE_RADIOBUTTON}
                name={field}
                caption={option}
                checked={option === value}
                onChange={() => handleChange(option)}
                disabled={disabled}
                showError={showErrors}
                error={error}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
